/**
 * Raport zarządu — teksty sekcji customers (`mbr.customers.*`). WŁAŚCICIEL: agent sekcji customers (docs/RAPORT-ZARZADU.md).
 * Podpięte już w i18n/mbr.ts — dopisuj klucze tutaj, nie w extra.ts. `en` typowane względem `pl`.
 * Własne kody uzgodnień sekcji: `checks: { KOD: '…' }` (panel szuka `mbr.checks.KOD`, potem `mbr.customers.checks.KOD`).
 */
import { mbrCustomersUiEn, mbrCustomersUiPl } from './mbr-customers-ui.ts';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrCustomersPl = {
  title: 'Klienci',
  description: 'Zamawiający, nowi, odzyskani, wstrzymani, utraceni i „wahadłowi”; bilans handlowców.',
  kpi: {
    ordering: 'Klienci zamawiający',
    gained: 'Pozyskani (nowi + odzyskani)',
    lost: 'Utraceni',
    balanceMargin: 'Bilans zysku brutto',
    balanceAmount: 'Bilans obrotu',
    gainedAmount: 'Obrót pozyskanych',
    lostAmount: 'Obrót utraconych',
    excelNote: 'jak w Excelu (utrata po 1 mies.): {count}',
  },
  status: {
    NEW: 'Nowi',
    RECOVERED: 'Odzyskani',
    ACTIVE: 'Aktywni',
    PAUSED: 'Wstrzymani',
    LOST: 'Utraceni',
    ALTERNATING: '„Wahadłowi”',
    INACTIVE: 'Utraceni wcześniej',
    GAINED: 'Pozyskani',
    ALL: 'Wszyscy',
  },
  statusHint: {
    NEW: 'Pierwsze zamówienie w historii.',
    RECOVERED: 'Wrócili po co najmniej {months} mies. bez zamówienia (sezon się nie liczy).',
    ACTIVE: 'Zamawiają; ewentualna przerwa krótsza niż {months} mies.',
    PAUSED: 'Bez zamówienia, ale to sezon klienta albo przerwa krótsza niż {months} mies.',
    LOST: 'W tym miesiącu minęło {months} mies. bez zamówienia.',
    ALTERNATING: 'Zamawiają co drugi miesiąc — w Excelu co miesiąc „tracone” i „pozyskiwane”.',
    INACTIVE: 'Utraceni w poprzednich miesiącach, nadal bez zamówienia.',
  },
  flows: {
    title: 'Przepływy klientów',
    description: 'Kliknij status, aby zobaczyć listę klientów.',
    status: 'Status',
    count: 'Liczba',
    base: 'Okres bazowy',
    amount: 'Obrót',
    margin: 'Zysk brutto',
    marginPct: 'Marża',
    amountHint: 'Pozyskani i aktywni — obrót okresu; utraceni i wstrzymani — obrót ostatniego miesiąca z zamówieniem.',
  },
  modes: {
    title: 'Ustawienia vs Excel',
    description:
      'Ten sam okres liczony wg ustawień (utrata po {months} mies., sezony, „wahadłowi”) i tak jak w Excelu klienta.',
    SETTINGS: 'Wg ustawień (N = {months})',
    EXCEL: 'Jak w Excelu (N = 1)',
    gained: 'Pozyskani',
    lost: 'Utraceni',
    balanceAmount: 'Bilans obrotu',
    balanceMargin: 'Bilans zysku brutto',
    diffNote: 'Różnica to głównie klienci „wahadłowi” i sezonowi, których Excel liczy jako utraconych i odzyskanych.',
  },
  bridge: {
    title: 'Most zamawiających',
    description: '{previousPeriod} → {period}: skąd się wzięła zmiana liczby zamawiających.',
    previous: 'Zamawiający {period}',
    gained: '+ nowi i odzyskani',
    returned: '+ wrócili po krótkiej przerwie',
    stopped: '− przestali zamawiać',
    current: 'Zamawiający {period}',
    formula: 'Wzór Excela: {previous} + {gained} − {lost} utraconych = {formula}; różnica {diff}.',
  },
  reps: {
    title: 'Bilans handlowców',
    description: 'Pozyskani minus utraceni wg obecnego opiekuna klienta.',
    metric: { margin: 'Zysk brutto', amount: 'Obrót' },
    owner: 'Handlowiec',
    gained: 'Pozyskani',
    lost: 'Utraceni',
    balance: 'Bilans',
    none: 'Brak pozyskanych i utraconych w okresie.',
    noOwner: 'bez opiekuna',
  },
  list: {
    title: 'Lista klientów',
    mode: 'Tryb',
    name: 'Klient',
    owner: 'Opiekun',
    amount: 'Obrót okresu',
    reference: 'Obrót przed przerwą',
    lastOrder: 'Ostatnie zamówienie',
    inactive: 'Bez zamówienia',
    months: '{count} mies.',
    seasonal: 'sezon',
    alternating: 'wahadłowy',
    empty: 'Brak klientów w tym statusie.',
    selectAll: 'Zaznacz wszystkich',
    select: 'Zaznacz klienta',
    selected: 'Zaznaczono: {count}',
    task: 'Zadanie',
    taskOpen: 'Zadanie otwarte',
    taskDone: 'Zadanie zamknięte',
    createTask: 'Utwórz zadanie: odzyskaj klienta',
    createTasks: 'Utwórz zadania „odzyskaj” ({count})',
    taskTitle: 'Odzyskaj klienta:',
    created: 'Utworzono zadań: {created}; już istniało: {exists}; pominięto: {skipped}.',
    createdOne: 'Zadanie utworzone i przypisane opiekunowi klienta.',
    existsOne: 'Zadanie „odzyskaj” dla tego klienta już jest otwarte.',
    notRecoverable: 'Klient zamówił w tym okresie — nie ma czego odzyskiwać.',
    openCard: 'Otwórz kartę klienta',
  },
  seasons: {
    title: 'Sezony klientów',
    description:
      'Miesiące, w których klienci segmentu zwykle nie zamawiają (np. szkoły w wakacje) — wtedy są „wstrzymani”, nie „utraceni”.',
    segment: 'Segment',
    customers: 'Klienci',
    configured: 'Z sezonem',
    months: 'Miesiące bez zamówień',
    suggested: 'Propozycja: {months}',
    save: 'Zapisz',
    saved: 'Sezon segmentu {segment} zapisany ({count} klientów).',
    none: 'Brak segmentów klientów.',
    readOnly: 'Zmiana sezonów wymaga uprawnienia do ustawień raportu.',
    seasonal: 'Klienci z sezonem w portfelu: {count}',
  },
  ui: mbrCustomersUiPl,
  checks: {
    CUSTOMER_FLOW_BRIDGE:
      'Zamawiający {current} vs wzór „poprzedni + pozyskani − utraceni” = {formula} (różnica {diffCount}): wrócili po krótkiej przerwie {returned}, przestali zamawiać bez utraty {stoppedNotLost}, utraceni z ostatnim zamówieniem przed poprzednim miesiącem {lostEarlier}.',
    MARGIN_MISSING_NOT_ZERO:
      'Klienci z obrotem bez kosztu towaru: {count} — marża nieznana (nie 0 i nie 100%), pominięta w zysku brutto.',
    CUSTOMER_HISTORY_TOO_SHORT:
      'Historia zbyt krótka do klasyfikacji: {count} klientów zamawia od początku danych ({historyStart}) lub wcześniej niż {months} mies. po nim — liczeni jako zamawiający, nie jako nowi ani odzyskani.',
  },
};

export const mbrCustomersEn: Shape<typeof mbrCustomersPl> = {
  title: 'Customers',
  description: 'Ordering, new, recovered, paused, lost and alternating customers; rep balance.',
  kpi: {
    ordering: 'Ordering customers',
    gained: 'Gained (new + recovered)',
    lost: 'Lost',
    balanceMargin: 'Gross profit balance',
    balanceAmount: 'Turnover balance',
    gainedAmount: 'Turnover of gained',
    lostAmount: 'Turnover of lost',
    excelNote: 'as in the Excel (lost after 1 month): {count}',
  },
  status: {
    NEW: 'New',
    RECOVERED: 'Recovered',
    ACTIVE: 'Active',
    PAUSED: 'Paused',
    LOST: 'Lost',
    ALTERNATING: 'Alternating',
    INACTIVE: 'Lost earlier',
    GAINED: 'Gained',
    ALL: 'All',
  },
  statusHint: {
    NEW: 'First order ever.',
    RECOVERED: 'Back after at least {months} months without an order (season not counted).',
    ACTIVE: 'Ordering; any break shorter than {months} months.',
    PAUSED: 'No order, but it is the customer’s season or a break shorter than {months} months.',
    LOST: '{months} months without an order reached this month.',
    ALTERNATING: 'Order every other month — the Excel counts them lost and gained every month.',
    INACTIVE: 'Lost in previous months, still not ordering.',
  },
  flows: {
    title: 'Customer flows',
    description: 'Click a status to see the customer list.',
    status: 'Status',
    count: 'Count',
    base: 'Base period',
    amount: 'Turnover',
    margin: 'Gross profit',
    marginPct: 'Margin',
    amountHint: 'Gained and active — turnover of the period; lost and paused — turnover of the last month with an order.',
  },
  modes: {
    title: 'Settings vs Excel',
    description:
      'The same period by the settings (lost after {months} months, seasons, alternating) and as in the client’s Excel.',
    SETTINGS: 'By settings (N = {months})',
    EXCEL: 'As in the Excel (N = 1)',
    gained: 'Gained',
    lost: 'Lost',
    balanceAmount: 'Turnover balance',
    balanceMargin: 'Gross profit balance',
    diffNote: 'The difference is mostly alternating and seasonal customers the Excel counts as lost and won back.',
  },
  bridge: {
    title: 'Ordering bridge',
    description: '{previousPeriod} → {period}: where the change in ordering customers comes from.',
    previous: 'Ordering {period}',
    gained: '+ new and recovered',
    returned: '+ back after a short break',
    stopped: '− stopped ordering',
    current: 'Ordering {period}',
    formula: 'Excel formula: {previous} + {gained} − {lost} lost = {formula}; difference {diff}.',
  },
  reps: {
    title: 'Rep balance',
    description: 'Gained minus lost by the customer’s current owner.',
    metric: { margin: 'Gross profit', amount: 'Turnover' },
    owner: 'Sales rep',
    gained: 'Gained',
    lost: 'Lost',
    balance: 'Balance',
    none: 'No gained or lost customers in the period.',
    noOwner: 'no owner',
  },
  list: {
    title: 'Customer list',
    mode: 'Mode',
    name: 'Customer',
    owner: 'Owner',
    amount: 'Period turnover',
    reference: 'Turnover before the break',
    lastOrder: 'Last order',
    inactive: 'No order for',
    months: '{count} mo.',
    seasonal: 'season',
    alternating: 'alternating',
    empty: 'No customers in this status.',
    selectAll: 'Select all',
    select: 'Select customer',
    selected: 'Selected: {count}',
    task: 'Task',
    taskOpen: 'Task open',
    taskDone: 'Task closed',
    createTask: 'Create task: recover the customer',
    createTasks: 'Create “recover” tasks ({count})',
    taskTitle: 'Recover the customer:',
    created: 'Tasks created: {created}; already open: {exists}; skipped: {skipped}.',
    createdOne: 'Task created and assigned to the customer’s owner.',
    existsOne: 'A “recover” task for this customer is already open.',
    notRecoverable: 'The customer ordered in this period — nothing to recover.',
    openCard: 'Open the customer card',
  },
  seasons: {
    title: 'Customer seasons',
    description:
      'Months in which customers of a segment normally do not order (e.g. schools in the holidays) — they are “paused”, not “lost”.',
    segment: 'Segment',
    customers: 'Customers',
    configured: 'With a season',
    months: 'Months without orders',
    suggested: 'Suggested: {months}',
    save: 'Save',
    saved: 'Season of segment {segment} saved ({count} customers).',
    none: 'No customer segments.',
    readOnly: 'Changing seasons requires the report settings permission.',
    seasonal: 'Customers with a season in the portfolio: {count}',
  },
  ui: mbrCustomersUiEn,
  checks: {
    CUSTOMER_FLOW_BRIDGE:
      'Ordering {current} vs the formula “previous + gained − lost” = {formula} (difference {diffCount}): back after a short break {returned}, stopped without being lost {stoppedNotLost}, lost with the last order before the previous month {lostEarlier}.',
    MARGIN_MISSING_NOT_ZERO:
      'Customers with turnover but no goods cost: {count} — margin unknown (not 0 and not 100%), left out of gross profit.',
    CUSTOMER_HISTORY_TOO_SHORT:
      'History too short to classify: {count} customers order from the start of the data ({historyStart}) or less than {months} months after it — counted as ordering, not as new or recovered.',
  },
};
